import React, { Children, isValidElement, useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDownIcon, SearchIcon, TickIcon } from "evergreen-ui";
import { useUiLanguage } from "../uiLanguage";
import { getVisualViewportBounds, placeFloatingPanel } from "./floatingPosition";

export const Brand = ({ compact = false }) => (
  <div className={`is2-brand${compact ? " is2-brand--compact" : ""}`}>
    <img src="/invoice-studio-v2.svg" alt="" className="is2-brand__mark" />
    <div className="is2-brand__type"><strong>Invoice</strong><span>Studio</span></div>
  </div>
);

export const IconButton = ({ label, children, className = "", ...props }) => {
  const { t } = useUiLanguage();
  return <button className={`is2-icon-button ${className}`} aria-label={t(label)} title={t(label)} {...props}>{children}</button>;
};

export const AvatarImage = ({ src, fallback, className = "", alt = "" }) => {
  const [failedSource, setFailedSource] = useState("");
  useEffect(() => setFailedSource(""), [src]);
  return <span className={className}>{src && failedSource !== src
    ? <img src={src} alt={alt} onError={() => setFailedSource(src)} />
    : fallback}</span>;
};

const optionListFromChildren = (children) => Children.toArray(children)
  .filter(isValidElement)
  .map((child) => {
    const parts = Children.toArray(child.props.children);
    const label = parts.every((part) => typeof part === "string" || typeof part === "number")
      ? parts.join("")
      : child.props.children;
    return { value: String(child.props.value ?? label ?? ""), label, disabled: Boolean(child.props.disabled) };
  });

export const CustomSelect = ({
  children,
  options,
  value,
  defaultValue,
  onChange,
  disabled = false,
  required = false,
  className = "",
  menuClassName = "",
  name,
  renderOption,
  renderValue,
  placeholder = "Choose an option",
  searchable,
  searchThreshold = 8,
  searchPlaceholder = "Search options",
  onOpen,
  onSearchChange,
  loading = false,
  hasMore = false,
  onLoadMore,
  loadMoreLabel = "Load more",
  ...props
}) => {
  const { t } = useUiLanguage();
  const generatedId = useId().replace(/:/g, "");
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const searchRef = useRef(null);
  const positionedAnchor = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue ?? "");
  const [activeIndex, setActiveIndex] = useState(0);
  const [menuStyle, setMenuStyle] = useState({});
  const normalizedOptions = useMemo(() => (options || optionListFromChildren(children)).map((option) => ({
    ...option,
    value: String(option.value ?? option.id ?? ""),
    label: option.label ?? option.name ?? String(option.value ?? option.id ?? ""),
  })), [children, options]);
  const controlled = value !== undefined;
  const currentValue = String(controlled ? value ?? "" : uncontrolledValue ?? "");
  const selected = normalizedOptions.find((option) => option.value === currentValue);
  const canSearch = searchable ?? normalizedOptions.length >= searchThreshold;
  const visibleOptions = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase();
    return normalizedOptions
      .map((option, index) => ({ option, index }))
      .filter(({ option }) => {
        if (!normalizedQuery) return true;
        const label = typeof option.label === "string" ? t(option.label) : option.searchText || option.value;
        return `${label} ${option.value}`.toLocaleLowerCase().includes(normalizedQuery);
      });
  }, [normalizedOptions, query, t]);
  const listboxId = `is2-select-${generatedId}`;

  const positionMenu = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    positionedAnchor.current = { top: rect.top, left: rect.left };
    const viewport = getVisualViewportBounds();
    const width = Math.max(rect.width, Math.min(320, viewport.width - 20));
    const position = placeFloatingPanel({ anchor: rect, width, height: 280, viewport });
    setMenuStyle({
      position: "fixed",
      width: position.width,
      maxHeight: position.maxHeight,
      left: position.left,
      top: position.top,
    });
  };

  const choose = (nextValue) => {
    const normalizedValue = String(nextValue ?? "");
    if (!controlled) setUncontrolledValue(normalizedValue);
    onChange?.({ target: { value: normalizedValue, name } });
    setOpen(false);
    window.requestAnimationFrame(() => triggerRef.current?.focus({ preventScroll: true }));
  };

  const openMenu = () => {
    if (disabled) return;
    onOpen?.();
    const selectedIndex = Math.max(0, normalizedOptions.findIndex((option) => option.value === currentValue));
    setQuery("");
    onSearchChange?.("");
    setActiveIndex(selectedIndex);
    positionMenu();
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return undefined;
    positionMenu();
    const closeOnOutside = (event) => {
      if (triggerRef.current?.contains(event.target) || menuRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    const closeOnViewportChange = (event) => {
      if (event.type === "scroll" && event.target instanceof Node && menuRef.current?.contains(event.target)) return;
      // A scroll queued before opening must not dismiss a menu whose anchor never moved.
      if (event.type === "scroll") {
        const rect = triggerRef.current?.getBoundingClientRect(), anchor = positionedAnchor.current;
        if (rect && anchor && Math.abs(rect.top - anchor.top) < 1 && Math.abs(rect.left - anchor.left) < 1) return;
      }
      setOpen(false);
    };
    document.addEventListener("pointerdown", closeOnOutside);
    window.addEventListener("resize", closeOnViewportChange);
    window.addEventListener("scroll", closeOnViewportChange, true);
    window.visualViewport?.addEventListener("resize", positionMenu);
    window.visualViewport?.addEventListener("scroll", positionMenu);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutside);
      window.removeEventListener("resize", closeOnViewportChange);
      window.removeEventListener("scroll", closeOnViewportChange, true);
      window.visualViewport?.removeEventListener("resize", positionMenu);
      window.visualViewport?.removeEventListener("scroll", positionMenu);
    };
  }, [open]);

  useEffect(() => {
    if (!open || !canSearch) return undefined;
    const frame = window.requestAnimationFrame(() => searchRef.current?.focus({ preventScroll: true }));
    return () => window.cancelAnimationFrame(frame);
  }, [canSearch, open]);

  const moveActive = (direction) => {
    const enabledOptions = visibleOptions.filter(({ option }) => !option.disabled);
    if (!enabledOptions.length) return;
    const currentPosition = enabledOptions.findIndex(({ index }) => index === activeIndex);
    const nextPosition = currentPosition < 0
      ? direction > 0 ? 0 : enabledOptions.length - 1
      : (currentPosition + direction + enabledOptions.length) % enabledOptions.length;
    const nextIndex = enabledOptions[nextPosition].index;
    setActiveIndex(nextIndex);
    window.requestAnimationFrame(() => menuRef.current?.querySelector(`[data-option-index="${nextIndex}"]`)?.scrollIntoView({ block: "nearest" }));
  };

  const handleKeyDown = (event) => {
    if (["ArrowDown", "ArrowUp"].includes(event.key)) {
      event.preventDefault();
      if (!open) openMenu();
      else moveActive(event.key === "ArrowDown" ? 1 : -1);
      return;
    }
    if (["Enter", " "].includes(event.key)) {
      event.preventDefault();
      if (!open) openMenu();
      else if (!normalizedOptions[activeIndex]?.disabled) choose(normalizedOptions[activeIndex]?.value);
      return;
    }
    if (event.key === "Escape" && open) {
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
    }
  };

  const handleSearchKeyDown = (event) => {
    if (["ArrowDown", "ArrowUp"].includes(event.key)) {
      event.preventDefault();
      moveActive(event.key === "ArrowDown" ? 1 : -1);
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      const active = normalizedOptions[activeIndex];
      if (visibleOptions.some(({ index }) => index === activeIndex) && !active?.disabled) choose(active.value);
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
      window.requestAnimationFrame(() => triggerRef.current?.focus({ preventScroll: true }));
    }
  };

  const updateQuery = (event) => {
    const nextQuery = event.target.value;
    setQuery(nextQuery);
    onSearchChange?.(nextQuery);
    const normalizedQuery = nextQuery.trim().toLocaleLowerCase();
    const firstMatch = normalizedOptions.findIndex((option) => {
      if (option.disabled) return false;
      if (!normalizedQuery) return true;
      const label = typeof option.label === "string" ? t(option.label) : option.searchText || option.value;
      return `${label} ${option.value}`.toLocaleLowerCase().includes(normalizedQuery);
    });
    if (firstMatch >= 0) setActiveIndex(firstMatch);
  };

  const dataCy = props["data-cy"];
  const ariaLabel = props["aria-label"];
  return <>
    <select
      className="is2-custom-select__native"
      data-cy={dataCy}
      aria-hidden="true"
      tabIndex={-1}
      name={name}
      value={currentValue}
      disabled={disabled}
      required={required}
      onChange={(event) => choose(event.target.value)}
    >
      {normalizedOptions.map((option) => <option key={option.value} value={option.value} disabled={option.disabled}>{typeof option.label === "string" ? t(option.label) : typeof option.label === "number" ? option.label : option.value}</option>)}
    </select>
    <div className={`is2-custom-select${open ? " is-open" : ""}${disabled ? " is-disabled" : ""}${className ? ` ${className}` : ""}`}>
    <button
      ref={triggerRef}
      type="button"
      className="is2-custom-select__trigger"
      data-cy={dataCy ? `${dataCy}-button` : undefined}
      role="combobox"
      aria-label={ariaLabel}
      aria-haspopup="listbox"
      aria-expanded={open}
      aria-controls={listboxId}
      aria-required={required || undefined}
      disabled={disabled}
      onClick={() => open ? setOpen(false) : openMenu()}
      onKeyDown={handleKeyDown}
    >
      <span className={!selected ? "is-placeholder" : ""}>{selected ? renderValue?.(selected) || (typeof selected.label === "string" ? t(selected.label) : selected.label) : t(placeholder)}</span>
      <ChevronDownIcon size={15} />
    </button>
    {open ? createPortal(<div ref={menuRef} id={listboxId} className={`is2-custom-select__menu${canSearch ? " is-searchable" : ""}${menuClassName ? ` ${menuClassName}` : ""}`} role="listbox" aria-label={ariaLabel || name || "Options"} style={menuStyle}>
      {canSearch ? <label className="is2-custom-select__search"><SearchIcon size={14} /><input ref={searchRef} type="search" value={query} onChange={updateQuery} onKeyDown={handleSearchKeyDown} placeholder={t(searchPlaceholder)} aria-label={t(searchPlaceholder)} autoComplete="off" /></label> : null}
      {visibleOptions.map(({ option, index }) => <button
        key={option.value}
        type="button"
        role="option"
        data-option-index={index}
        aria-selected={option.value === currentValue}
        className={`${option.value === currentValue ? "is-selected" : ""}${index === activeIndex ? " is-active" : ""}`}
        disabled={option.disabled}
        onMouseEnter={() => setActiveIndex(index)}
        onClick={() => choose(option.value)}
      >
        <span>{renderOption?.(option) || (typeof option.label === "string" ? t(option.label) : option.label)}</span>
        {option.value === currentValue ? <TickIcon size={14} /> : null}
      </button>)}
      {!visibleOptions.length && !loading ? <span className="is2-custom-select__empty">{t("No matching options")}</span> : null}
      {loading ? <span className="is2-custom-select__status" aria-live="polite">{t("Loading options...")}</span> : null}
      {hasMore && onLoadMore ? <button
        type="button"
        className="is2-custom-select__load-more"
        disabled={loading}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onLoadMore();
        }}
      ><span>{t(loadMoreLabel)}</span></button> : null}
    </div>, document.body) : null}
    </div>
  </>;
};

export const ViewHeading = ({ eyebrow, title, description, actions }) => {
  const { t } = useUiLanguage();
  return <header className="is2-view-heading">
    <div>
      {eyebrow ? <p className="is2-eyebrow">{t(eyebrow)}</p> : null}
      <h1>{t(title)}</h1>
      {description ? <p>{t(description)}</p> : null}
    </div>
    {actions ? <div className="is2-view-heading__actions">{actions}</div> : null}
  </header>;
};

export const PopoverLayer = ({ children, close, className = "" }) => (
  <div className={`is2-popover-layer ${className}`} role="presentation" onMouseDown={(event) => event.target === event.currentTarget && close()}>
    {children}
  </div>
);

export const ModalLayer = ({ children, close, className = "" }) => {
  const layerRef = useRef(null);
  const previousFocusRef = useRef(typeof document !== "undefined" ? document.activeElement : null);
  const closeRef = useRef(close);
  closeRef.current = close;

  useEffect(() => {
    const previousFocus = previousFocusRef.current;
    const previousBodyStyle = {
      overflow: document.body.style.overflow,
      position: document.body.style.position,
      top: document.body.style.top,
      width: document.body.style.width,
    };
    const lockedScrollY = window.scrollY || 0;
    const modalCount = Number(document.body.dataset.invoiceStudioModalCount || 0) + 1;
    document.body.dataset.invoiceStudioModalCount = String(modalCount);
    document.body.classList.add("is2-modal-open");
    if (modalCount === 1) {
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.top = `-${lockedScrollY}px`;
      document.body.style.width = "100%";
    }
    const visualViewport = window.visualViewport;
    let focusTimer = null;
    const syncVisualViewport = () => {
      const layer = layerRef.current;
      if (!layer) return;
      const height = visualViewport?.height || window.innerHeight;
      const offsetTop = visualViewport?.offsetTop || 0;
      layer.style.setProperty("--is2-modal-viewport-height", `${Math.round(height)}px`);
      layer.style.setProperty("--is2-modal-viewport-top", `${Math.round(offsetTop)}px`);
      const active = document.activeElement;
      if (visualViewport && active && layer.contains(active) && ["INPUT", "SELECT", "TEXTAREA"].includes(active.tagName)) {
        window.clearTimeout(focusTimer);
        focusTimer = window.setTimeout(() => active.scrollIntoView?.({ block: "center", inline: "nearest" }), 60);
      }
    };
    syncVisualViewport();
    visualViewport?.addEventListener("resize", syncVisualViewport);
    visualViewport?.addEventListener("scroll", syncVisualViewport);
    const focusTarget = layerRef.current?.querySelector("button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])");
    focusTarget?.focus();
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeRef.current();
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = [...(layerRef.current?.querySelectorAll("button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [href], [tabindex]:not([tabindex='-1'])") || [])];
      if (!focusable.length) {
        event.preventDefault();
        layerRef.current?.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      visualViewport?.removeEventListener("resize", syncVisualViewport);
      visualViewport?.removeEventListener("scroll", syncVisualViewport);
      const remainingModals = Math.max(0, Number(document.body.dataset.invoiceStudioModalCount || 1) - 1);
      if (remainingModals) document.body.dataset.invoiceStudioModalCount = String(remainingModals);
      else {
        delete document.body.dataset.invoiceStudioModalCount;
        document.body.classList.remove("is2-modal-open");
        document.body.style.overflow = previousBodyStyle.overflow;
        document.body.style.position = previousBodyStyle.position;
        document.body.style.top = previousBodyStyle.top;
        document.body.style.width = previousBodyStyle.width;
        if (lockedScrollY) window.scrollTo(0, lockedScrollY);
      }
      window.removeEventListener("keydown", handleKeyDown);
      previousFocus?.focus?.();
    };
  }, []);

  return <div ref={layerRef} className={`is2-modal-backdrop${className ? ` ${className}` : ""}`} role="presentation" tabIndex={-1} onMouseDown={(event) => event.target === event.currentTarget && close()}>
    {children}
  </div>;
};
